import type { Method, Species, Spot } from "../types";

const ALL_SOURCES = [
  "svfr.is",
  "veidikortid.is",
  "lax-a.is",
  "strengir.is",
  "fishpartner.is",
  "fluguveidi.is",
] as const;
export type SourceKey = (typeof ALL_SOURCES)[number];

export const DEFAULT_SOURCES: SourceKey[] = [...ALL_SOURCES];

export interface FilterState {
  q: string;
  date: string;
  species: "" | Species;
  method: "" | Method;
  enabledSources: Set<SourceKey>;
  onlyInSeason: boolean;
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export const initialFilters = (): FilterState => ({
  q: "",
  date: todayIso(),
  species: "",
  method: "",
  enabledSources: new Set<SourceKey>(DEFAULT_SOURCES),
  onlyInSeason: false,
});

export function isInSeasonOn(spot: Spot, isoDate: string): boolean | null {
  if (!spot.seasonStart || !spot.seasonEnd || !isoDate) return null;
  const md = isoDate.slice(5);
  const start = spot.seasonStart.slice(2);
  const end = spot.seasonEnd.slice(2);
  if (start <= end) return md >= start && md <= end;
  return md >= start || md <= end;
}

export function normalizeSearch(s: string): string {
  return String(s ?? "")
    .toLowerCase()
    .replace(/ð/g, "d")
    .replace(/þ/g, "th")
    .replace(/æ/g, "ae")
    .replace(/ö/g, "o")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export function spotSourceKey(spot: Spot): SourceKey {
  const n = spot.source?.name;
  if (
    n === "svfr.is" ||
    n === "veidikortid.is" ||
    n === "lax-a.is" ||
    n === "strengir.is" ||
    n === "fishpartner.is" ||
    n === "fluguveidi.is"
  ) {
    return n;
  }
  return "svfr.is";
}

export function spotMatchesFilters(spot: Spot, f: FilterState): boolean {
  if (f.q) {
    const needle = normalizeSearch(f.q);
    const hay = normalizeSearch([spot.name, spot.river].filter(Boolean).join(" "));
    if (!hay.includes(needle)) return false;
  }
  if (!f.enabledSources.has(spotSourceKey(spot))) return false;
  if (f.species && !spot.species.includes(f.species)) return false;
  if (f.method && !spot.methods.includes(f.method)) return false;
  if (f.onlyInSeason) {
    const inSeason = isInSeasonOn(spot, f.date || todayIso());
    if (inSeason === false) return false;
  }
  return true;
}
